import { useEffect, useRef, useState } from 'react';
import type { DealImage } from '../api/types';
import { markPhotoBroken, useStandInPhoto } from '../lib/standInPhotos';

/**
 * The deal's photos: one large, the rest as a thumbnail strip.
 *
 * WHY THIS IS A TABLIST AND NOT A ROW OF BUTTONS
 *
 * Picking a photo is choosing which of several panels is displayed, which is
 * precisely what the tab pattern describes — so the strip gets `role="tablist"`,
 * arrow-key navigation, and roving tabindex. The alternative, four ordinary
 * buttons, puts four stops in the tab order between the breadcrumb and the buy
 * button on a page whose entire purpose is reaching the buy button.
 *
 * ARROW KEYS MOVE THE SELECTION, TAB LEAVES THE STRIP. That is the whole point of
 * roving tabindex: only the active thumb is tabbable, so the strip costs one stop
 * no matter how many photos a business uploads.
 *
 * ALT TEXT IS THE BUSINESS'S, OR NOTHING. `altText` is null for a decorative shot,
 * and null renders alt="" — the correct markup for an image that adds nothing when
 * read aloud. Substituting the deal title would make a screen reader announce the
 * title once per photo, which is worse than silence.
 */
export function DealGallery({
  images,
  fallbackUrl,
  dealId,
  category,
}: {
  images: DealImage[];
  fallbackUrl: string | null;
  dealId: string;
  category: string;
}) {
  const [index, setIndex] = useState(0);

  // Photos that failed to load, by URL: dropped from the gallery.
  const [broken, setBroken] = useState<ReadonlySet<string>>(new Set());
  const stripRef = useRef<HTMLDivElement>(null);

  // A deal whose gallery has not been filled in still has a cover on Deal.ImageUrl.
  const own: DealImage[] = images.length > 0
    ? images
    : fallbackUrl === null ? [] : [{ url: fallbackUrl, altText: null }];
  const working = own.filter((image) => !broken.has(image.url));

  // None of its own work: borrow the same stand-in its card shows (lib/standInPhotos.ts).
  const standIn = useStandInPhoto(dealId, category, own.map((image) => image.url), working.length === 0);
  const shown: DealImage[] = working.length > 0
    ? working
    : standIn === null ? [] : [{ url: standIn, altText: null }];

  function onPhotoError(url: string) {
    markPhotoBroken(url);
    setBroken((prev) => new Set(prev).add(url));
  }

  // Guards against an out-of-range index if the deal changes under a mounted
  // component — navigating between two deals reuses this instance.
  useEffect(() => { setIndex(0); }, [images]);

  const active = shown[Math.min(index, shown.length - 1)];

  function onKeyDown(event: React.KeyboardEvent) {
    const last = shown.length - 1;

    const next =
      event.key === 'ArrowRight' ? (index === last ? 0 : index + 1)
        : event.key === 'ArrowLeft' ? (index === 0 ? last : index - 1)
          : event.key === 'Home' ? 0
            : event.key === 'End' ? last
              : null;

    if (next === null) return;

    event.preventDefault();
    setIndex(next);

    // Focus follows selection, which is what makes arrow keys feel like a real
    // control rather than something that changes a picture somewhere off-screen.
    const buttons = stripRef.current?.querySelectorAll('button');
    buttons?.[next]?.focus();
  }

  return (
    <div className="gallery">
      <div className="main">
        {active === undefined
          ? <div className="ph">deal photo</div>
          : (
            <img
              key={active.url}
              src={active.url}
              alt={active.altText ?? ''}
              onError={() => onPhotoError(active.url)}
            />
          )}
      </div>

      {/* One photo needs no picker. Rendering a strip of one is a control that
          cannot do anything, which is worse than no control. */}
      {shown.length > 1 && (
        <div
          className="thumbs"
          role="tablist"
          aria-label="Photos"
          ref={stripRef}
          onKeyDown={onKeyDown}
        >
          {shown.map((image, i) => (
            <button
              key={image.url}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={image.altText ?? `Photo ${i + 1} of ${shown.length}`}
              tabIndex={i === index ? 0 : -1}
              className={i === index ? 'thumb is-active' : 'thumb'}
              onClick={() => setIndex(i)}
            >
              <img src={image.url} alt="" loading="lazy" onError={() => onPhotoError(image.url)} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
